/* STRØMNINGEN — vannet som går gjennom merdene, tegnet som det beveger seg.
 *
 * HVORFOR DETTE FÅR BEVEGE SEG NÅR SVERMEN IKKE FÅR KODE NOE I SIN
 *   `sverm.ts` sier at fart og retning på fisken IKKE koder noe, og at et
 *   partikkelfelt over fjorden ville vært «en animasjon av en strøm ingen har
 *   målt». Begge deler står fortsatt. Forskjellen her er at det ER en måling
 *   bak: en time av varselet har en fart og en retning, og partiklene under
 *   flytter seg med nettopp den farten og nettopp den retningen. Bevegelsen er
 *   tallet. Det er den ene figuren en pil ikke får til — en pil må tegne fart
 *   som lengde, og lengde forveksles med avstand.
 *
 * HVA DEN LIKEVEL IKKE PÅSTÅR
 *   Partiklene lever i en korridor rundt hver merd og ingen andre steder.
 *   Modellens rutenett er omtrent en kilometer; å la dem drive fritt mellom
 *   ringene ville tegnet struktur på ti meter ut av en modell som ikke har noen
 *   under tusen. Så: vann gjennom DENNE merden, og svart sjø mellom dem.
 *   Korridoren er påstanden, og den er liten med vilje.
 *
 * HVORFOR EN GAIN, OG HVORFOR DEN ER ÆRLIG
 *   0,04 m/s er fire centimeter i sekundet. På kartet er det omtrent en piksel
 *   hvert tredje sekund — usynlig. Så farten skaleres opp med en fast faktor.
 *   Fast er poenget: to timer med ulik fart ser ulike ut, og forholdet mellom
 *   dem er riktig. Det er en tidslupe, ikke en oppdiktet strøm.
 *
 * ROLIG MODUS
 *   `prefers-reduced-motion` slår av løkka helt. Da står sjevronene i
 *   `stroem-fill` alene, og de sier det samme uten å bevege seg.
 */

/** Tidslupen. Fast, aldri normalisert mot vinduet — se filhodet.
 *
 *  Valgt mot krysningstid og ikke mot en følelse. Korridoren er 4,6 radier,
 *  altså omtrent 117 m på en 160-ring, og en dropp skal bruke noen få sekunder
 *  på den for å leses som renning:
 *
 *      0,025 m/s  svak        10,4 s   — så vidt i drift
 *      0,043 m/s  moderat      6,1 s   — medianen på denne kysten
 *      0,12  m/s  god          2,2 s
 *      0,40  m/s  sterk        0,7 s   — og da SKAL det se voldsomt ut
 *
 *  Forholdet mellom dem er riktig, som det må være når leseren skal se
 *  forskjell på to timer. */
const GAIN = 450

/** Korridoren rundt en merd, i radier. Partikler fødes ved `-FRA`, dør ved
 *  `+FRA`, og holder seg innenfor `BREI` på tvers. */
const FRA = 2.3
const BREI = 1.05

/** Under dette er ringen for liten på skjermen til at noe innenfor den kan
 *  leses. Samme terskel som svermen bruker, og av samme grunn. */
const MIN_PX = 26

export interface Stroemning {
  penId: string
  lon: number
  lat: number
  radiusM: number
  /** Grader med klokka fra nord, dit vannet går. */
  bearing: number
  /** m/s. */
  speed: number
  /** slack | weak | moderate | good | strong */
  band: string
  slack: boolean
}

interface Dropp {
  s: number      // langs strømmen, i radier
  q: number      // på tvers, i radier
  fart: number   // liten variasjon, så de ikke marsjerer i takt
  lang: number   // strekets lengde, i radier
  liv: number    // 0..1, for inn- og uttoning
}

const BAND: Record<string, string> = {
  slack: '#93ab9f',    // --faint
  weak: '#93ab9f',     // --faint
  moderate: '#b9cec3', // --muted
  good: '#b4d9c7',     // --signal
  strong: '#e9c46a',   // --warn
}

/** Hvor mange dropper en merd får. Flere når det renner mer: tettheten er
 *  farten sagt en gang til, i en kanal som overlever et raskt blikk. */
function antall(band: string): number {
  if (band === 'good' || band === 'strong') return 13
  if (band === 'moderate') return 9
  return 5
}

function nyDropp(spredt: boolean): Dropp {
  return {
    s: spredt ? Math.random() * 2 * FRA - FRA : -FRA,
    q: (Math.random() * 2 - 1) * BREI,
    fart: 0.75 + Math.random() * 0.5,
    lang: 0.22 + Math.random() * 0.3,
    liv: Math.random(),
  }
}

/** Start strømningen. Returnerer en funksjon som stopper den og tømmer lerretet.
 *
 *  `hent` kalles hver frame og ikke lukkes over, fordi timen under kartet kan
 *  skifte mens løkka går — og et felt som fortsatt renner forrige times vei er
 *  verre enn ingen figur. */
export function startStroemning(
  canvas: HTMLCanvasElement,
  map: any,
  hent: () => Stroemning[],
): () => void {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}

  const stille = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (stille) return () => {}

  const felt = new Map<string, Dropp[]>()
  let ram = 0
  let forrige = 0
  let levende = true

  /* DPR 1,5 OG IKKE 2, OG DET ER IKKE FINSLIPING.
     Dette lerretet ligger over et fullskjermskart som allerede holder en
     WebGL-kontekst, og ved siden av svermens lerret. Tre store, GPU-støttede
     flater på en retina-skjerm er nok til at nettleseren kaster WebGL-
     konteksten — kartet ble hvitt noen sekunder inn mens siden rundt levde
     videre. 1,5 er en fjerdedel mindre minne enn 2, og en komethale tåler det:
     den har ingen skarpe kanter å miste. */
  const DPR = Math.min(1.5, window.devicePixelRatio || 1)

  /* Målt ved oppstart og ved resize, ikke hver frame.
     `getBoundingClientRect()` tvinger layout, og å gjøre det seksti ganger i
     sekundet ved siden av et kart som selv tegner er den dyreste linja i fila
     for ingenting: boksen endrer seg bare når vinduet gjør det. */
  let boks = { width: 0, height: 0 }

  const maal = () => {
    const b = canvas.getBoundingClientRect()
    boks = { width: b.width, height: b.height }
    const w = Math.round(b.width * DPR), h = Math.round(b.height * DPR)
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
    }
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0)
  }
  maal()

  const tegn = (naa: number) => {
    if (!levende) return
    const b = boks
    const dt = forrige ? Math.min(0.05, (naa - forrige) / 1000) : 0
    forrige = naa
    ctx.clearRect(0, 0, b.width, b.height)

    for (const s of hent()) {
      const c = map.project([s.lon, s.lat])

      /* Radien i piksler, målt og ikke gjettet: prosjekter et punkt én radius
         unna og se hvor langt det havnet. Det holder gjennom zoom og over
         breddegrader uten en eneste konstant. */
      const dLat = s.radiusM / 111320
      const kant = map.project([s.lon, s.lat + dLat])
      const R = Math.hypot(kant.x - c.x, kant.y - c.y)
      if (R < MIN_PX) continue
      if (c.x < -R * 4 || c.y < -R * 4
          || c.x > b.width + R * 4 || c.y > b.height + R * 4) continue

      /* Stille vann får ingen dropper. En figur som står stille og en som
         renner sakte ser like ut i ett øyeblikksbilde, og de betyr forskjellige
         ting — så den ene tegnes ikke. */
      if (s.slack || s.speed <= 0) { felt.delete(s.penId); continue }

      const n = antall(s.band)
      let dropper = felt.get(s.penId)
      if (!dropper || dropper.length !== n) {
        dropper = Array.from({ length: n }, () => nyDropp(true))
        felt.set(s.penId, dropper)
      }

      // Retningen dit vannet går, i skjermens akser. Kartet står låst i nord,
      // så peilingen ER skjermvinkelen.
      const rad = (s.bearing * Math.PI) / 180
      const ux = Math.sin(rad), uy = -Math.cos(rad)   // y peker ned på skjerm
      const vx = -uy, vy = ux                          // på tvers

      // Radier per sekund: farten gjennom en ring hvis radius er radiusM.
      const perSek = (s.speed * GAIN) / s.radiusM
      const farge = BAND[s.band] ?? BAND.moderate!

      /* Farge og bredde settes én gang per merd og ikke én gang per dropp.
         Tilstandsbytter på en 2D-kontekst er det som koster her, ikke
         strekene. */
      ctx.lineCap = 'round'
      ctx.strokeStyle = farge
      ctx.lineWidth = Math.max(1, R * 0.075)

      for (const d of dropper) {
        d.s += perSek * d.fart * dt
        d.liv = Math.min(1, d.liv + dt * 0.9)
        if (d.s > FRA) Object.assign(d, nyDropp(false), { liv: 0 })

        // Toner inn ved fødsel og ut mot enden, så ingen dropp blinker.
        const kant2 = Math.min(1, (FRA - Math.abs(d.s)) / 0.55)
        const a = Math.max(0, Math.min(1, kant2)) * d.liv
        if (a <= 0.01) continue

        const hx = c.x + (d.s * ux + d.q * vx) * R
        const hy = c.y + (d.s * uy + d.q * vy) * R
        const tx = hx - ux * d.lang * R
        const ty = hy - uy * d.lang * R

        /* En komet og ikke en prikk: hodet lyst, halen svak. Det er halen som
           gjør at et stillbilde av dette fortsatt peker et sted.

           To streker med hver sin alfa, ikke en gradient. `createLinearGradient`
           lager et objekt per dropp per frame — sju merder ganger tretten
           dropper ganger seksti er fire tusen objekter i sekundet å samle inn,
           og søppelrydding midt i en tegneløkke er nettopp det som gjør en
           animasjon hakkete. */
        ctx.globalAlpha = a * 0.30
        ctx.beginPath()
        ctx.moveTo(tx, ty)
        ctx.lineTo(hx, hy)
        ctx.stroke()

        ctx.globalAlpha = a * 0.9
        ctx.beginPath()
        ctx.moveTo(hx - ux * d.lang * R * 0.32, hy - uy * d.lang * R * 0.32)
        ctx.lineTo(hx, hy)
        ctx.stroke()
      }
      ctx.globalAlpha = 1
    }

    ram = requestAnimationFrame(trygg)
  }

  /* En feil her skal slå av laget, ikke gjenta seg seksti ganger i sekundet.
     Kartet under er hovedsaken; strømmen er et lag oppå det, og et lag som
     kaster må forsvinne stille i stedet for å ta siden med seg. */
  const trygg = (naa: number) => {
    try {
      tegn(naa)
    } catch {
      levende = false
      if (ram) cancelAnimationFrame(ram)
      ram = 0
      ctx.clearRect(0, 0, boks.width, boks.height)
    }
  }

  ram = requestAnimationFrame(trygg)

  /* En fane i bakgrunnen skal ikke tegne. Det er den ene optimaliseringa som
     betyr noe her: sju merder ganger tretten dropper er ingenting, men en
     rAF-løkke i tjue faner er en vifte som går. */
  const synlig = () => {
    if (document.visibilityState === 'visible') {
      if (!ram && levende) { forrige = 0; ram = requestAnimationFrame(trygg) }
    } else if (ram) {
      cancelAnimationFrame(ram)
      ram = 0
    }
  }
  document.addEventListener('visibilitychange', synlig)
  window.addEventListener('resize', maal)

  return () => {
    levende = false
    if (ram) cancelAnimationFrame(ram)
    document.removeEventListener('visibilitychange', synlig)
    window.removeEventListener('resize', maal)
    ctx.clearRect(0, 0, boks.width, boks.height)
  }
}
